import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import vueDebounce from 'vue-debounce'
import App from './App.vue'
import router from './router'
import store from './store'
import axios from 'axios'
import en from '@/locales/en.json'
import nl from '@/locales/nl.json'
import fr from '@/locales/fr.json'
import VueI18n from 'vue-i18n'
import { seedSessionCountdown } from '@/composables/useSession'
import { handleResponseError } from '@/utils/handleResponseError'
import { ServiceFactory } from '@/services/ServiceFactory'
import { can, Permission } from '@/composables/useGuard'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { SettingsStore } from '@/composables/useSettings'
import '@/plugins/icons'

Vue.config.productionTip = false

Vue.use(VueI18n)
Vue.use(vueDebounce)
Vue.component('VSelect', vSelect)

export const isRole = (role: string) =>
  ServiceFactory.createPermissionService().is(role)

axios.interceptors.request.use((config) => {
  const token = store.state.accessToken

  if (token) config.headers.Authorization = `Bearer ${token}`

  return config
}, null)

// The countdown rides real responses rather than polling for itself: the API
// puts X-Session-Expires-In on every authenticated response, so this costs no
// extra requests and cannot drift far.
axios.interceptors.response.use((response) => {
  const remaining = response.headers?.['x-session-expires-in']

  if (remaining !== undefined) seedSessionCountdown(Number(remaining))

  return response
}, null)

axios.interceptors.response.use(null, handleResponseError)

axios.defaults.baseURL = process.env.VUE_APP_API_URL

const settings = await SettingsStore.get()

Vue.mixin({
  methods: {
    navigateTo(route) {
      return this.$router.push(route)
    },

    can(permission: Permission) {
      return can(permission)
    },

    is(role) {
      return ServiceFactory.createPermissionService().is(role)
    },

    // TODO: Remove this when the backend is updated
    hasOption(name: string): boolean {
      switch (name) {
        case 'basicOptIn':
          return settings?.clients?.optIn?.hasBypass ?? false
        case 'multiLanguage':
          return settings?.general?.multiLanguage ?? false
        case 'issuedMaterials':
          return settings?.issuedMaterials?.isActive ?? false
        case 'materialBatchNumbers':
          return settings?.issuedMaterials?.batchNumbers ?? false
        case 'interactionNote':
          return settings?.interaction?.showNote ?? false
        case 'clientNote':
          return settings?.clients?.showNote ?? false
        case 'locationNote':
          return settings?.locations?.showNote ?? false
        default:
          Sentry.captureException(
            new Error(`No option configured for "${name}"`),
          )
          return false
      }
    },

    // TODO: Remove this when the backend is updated
    hasModule(name: string): boolean {
      switch (name) {
        case 'optIn':
          return settings?.modules.optIn || false
        case 'cycle':
          // TODO: Probably should remove this because cycles are always enabled
          return true
        case 'tier':
          return settings?.modules.tier || false
        case 'target':
          return settings?.modules.target || false
        case 'rayon':
          return settings?.modules.rayon || false
        case 'activity':
          return settings?.activity?.isActive || false
        case 'omnichannel':
          return settings?.modules.omnichannel || false
        default:
          Sentry.captureException(
            new Error(`No module configured for "${name}"`),
          )
          return false
      }
    },
  },
})

const fallbackLocale = settings?.general?.defaultLanguage ?? 'nl'
let locale = fallbackLocale

if (store?.state?.authenticatedUser?.language) {
  locale = store.state.authenticatedUser.language
}

const i18n = new VueI18n({
  locale,
  fallbackLocale,
  messages: {
    nl,
    en,
    fr,
  },
})

if (process.env.VUE_APP_SENTRY_DSN) {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [],
    tracesSampleRate: 1,
  })
}

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount('#app')
